import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { hasFeatureAccessFromState } from "../../services/subscriptionService";
import {
    ConfigPhone,
    MatchOfficials,
    Player,
    SubscriptionItems,
    Templates,
    UserActionsCheckList,
} from "refsix-js-models";
import { filter, flatten, map, values } from "lodash/fp";
import { UserProfile } from "../../models/userProfile";
import { SubscriptionState } from "../../redux/models/subscriptionState";
import { AppLifecyclePhase, DatabaseStatus } from "../../redux/models/refsixState";
import { useMatches, useMatchesBy } from "./match";
import { getLanguageCode } from "../../utils/localeUtils";
import { useStatus } from "./useNetwork";
import { useEffect, useState } from "react";
import {
    getInvoice,
    getNextInvoice,
    getPlan,
    getPlanById,
    getPlans,
    getSchedule,
} from "../../services/StripeService";

export const useAppLifecyclePhase = (): AppLifecyclePhase =>
    useSelector((state: RootState) => state.appLifecycle.phase);

export const useUsername = (): string | undefined =>
    useSelector((state: RootState) => state.auth.session?.user_id);
export const useEmail = (): string | undefined =>
    useSelector((state: RootState) => state.currentUser?.profile?.email);
export const useInstalledAppVersion = (): string | undefined =>
    useSelector((state: RootState) => state.appStatus.installedVersion);

export const useUserLoggedIn = (): boolean =>
    useSelector((state: RootState) => state.auth.loggedIn);
export const useLoginOrRegisterComplete = (): boolean =>
    useSelector((state: RootState) => state.appStatus.loginOrRegisterComplete);
export const useShouldShowWhatsNew = (): boolean =>
    useSelector((state: RootState) => state.appStatus.showWhatsNew);

export const useHasFeatureAccess = (feature: string): boolean =>
    useSelector((state: RootState) =>
        hasFeatureAccessFromState(state.subscriptions, feature)
    );

export const usePlayersByTeam = (teamName: string): string[] => {
    const matches = useMatchesBy(
        (m) => m.homeTeam === teamName || m.awayTeam === teamName
    );
    const players = flatten(
        matches?.map((m) => (m.players && m.players[teamName]) || [])
    );
    const filteredPlayers = filter((p) => !p.teamOfficial, players);
    return map("name", filteredPlayers);
};

export const useMatchOfficials = (): string[] => {
    const matches = useMatches(); // Assuming useMatches() returns an array of match objects
    const officialNames: string[] = [];

    const officials: (MatchOfficials | undefined)[] = matches
        ? matches.map((match) => match.matchOfficials)
        : [];

    officials.forEach((officials) => {
        if (officials && typeof officials === "object") {
            Object.values(officials).forEach((name) => {
                if (name) {
                    officialNames.push(name);
                }
            });
        }
    });

    return officialNames;
};

export const useTeamOfficials = (): Player[] => {
    const matches = useMatches();

    const players = flatten(
        matches?.map((m) => (m.players && values(m.players)) || []).flat()
    );

    return players.filter((p) => p.teamOfficial);
};

export const useSettings = (): ConfigPhone | undefined =>
    useSelector((state: RootState) => state.settings.settings);

export const useProfile = (): UserProfile | undefined =>
    useSelector((state: RootState) => state.currentUser?.profile);
export const useTemplates = (): Templates | undefined =>
    useSelector((state: RootState) => state.templates.templates);

export const useSubscription = (): SubscriptionState | undefined =>
    useSelector((state: RootState) => state.subscriptions);
export const useStripeSubscription = (): SubscriptionItems | undefined => {
    const subs = useSubscription();

    const [stripeSubscription, setStripeSubscription] = useState<
        SubscriptionItems | undefined
    >(undefined);
    useEffect(() => {
        if (!subs) {
            return;
        }
        const activeStripeSubscriptions = subs.subscriptions.filter(
            (sub) => sub.provider === "stripe" && sub.status === "active"
        );
        if (activeStripeSubscriptions.length > 0) {
            setStripeSubscription(activeStripeSubscriptions[0]);
        }
    }, [subs]);
    return stripeSubscription;
};

export const useIsPro = (): boolean =>
    useSelector((state: RootState) => state.subscriptions?.isPro);
export const useIsBasic = (): boolean =>
    useSelector((state: RootState) => state.subscriptions?.isPlus);

export const useUserActionsCheckList = (): UserActionsCheckList =>
    useSelector((state: RootState) => state.userActionsCheckList);

export const useLocation = (): Location => window.location;

export const useIsLanguageJapanese = (): boolean => getLanguageCode() === "ja";
export const useIsLanguageEnglish = (): boolean => getLanguageCode() === "en";

export const useSessionExpired = (): boolean =>
    useSelector((state: RootState) => state.auth.sessionExpired || false);
export const useSessionExpiredModalDismissed = (): boolean =>
    useSelector(
        (state: RootState) =>
            state.appStatus.sessionExpiredModalDismissed || false
    );
export const useDbUpdatedAt = (): number | undefined =>
    useSelector((state: RootState) => state.appStatus.dbUpdatedAt);
export const useIsOnline = () => {
    const { networkStatus } = useStatus();
    return networkStatus?.connected || false;
};
export const useAppleLoginCache = (): any =>
    useSelector((state: RootState) => state.settings.appleLoginCache);

export const useDatabaseStatus = () =>
    useSelector((state: RootState) => state.matches.databaseStatus);

/** @deprecated Use useDatabaseStatus instead */
export const useLoadingMatches = (): boolean =>
    useSelector(
        (state: RootState) =>
            state.matches.databaseStatus === DatabaseStatus.LOADING ||
            state.matches.databaseStatus === DatabaseStatus.NOT_STARTED
    );

export const useShowOnBoardingFlow = (): boolean =>
    useSelector((state: RootState) => state.appStatus.showOnBoardingFlow);

export const useIntegrationsMessages = (): any =>
    useSelector(
        (state: RootState) => state.integrationsMessages.messages || []
    );

export const useCurrentStripePlan = () => {
    const sub = useStripeSubscription();
    const [plan, setPlan] = useState<any>(null);
    useEffect(() => {
        const fn = async () => {
            if (!sub) return;
            try {
                const currentPlan = await getPlanById(sub.planName);

                setPlan(currentPlan);
            } catch (error) {
                console.error("Error", error);
            }
        };
        fn();
    }, [sub]);
    return plan;
};
export const useStripeUpgradePlan = (
    interval: "year" | "month",
    planFeatures: string,
    isPublic: boolean
) => {
    const currentPlan = useCurrentStripePlan();

    const [plan, setPlan] = useState<any>(null);
    useEffect(() => {
        const fn = async () => {
            //if (!planFeatures) return;
            const plan = await getPlan(
                interval,
                planFeatures,
                isPublic,
                currentPlan?.currency
            );

            setPlan(plan);
        };
        fn();
    }, [interval, planFeatures, isPublic, currentPlan?.currency]);
    return plan;
};
export type StripePhase = {
    end_date: number;
    start_date: number;
    // and anything else
    [key: string]: any;
};
export type StripeSchedule = {
    current_phase: {
        end_date: number;
        start_date: number;
    };
    renewal_behavior: string;
    phases: StripePhase[];
};
export const useStripeSchedule = (): [StripeSchedule | undefined, Function] => {
    const [schedule, setSchedule] = useState<StripeSchedule>();
    useEffect(() => {
        refresh();
    }, []);

    const refresh = async () => {
        try {
            const schedule = await getSchedule();
            setSchedule(schedule as StripeSchedule);
        } catch (error) {}
    };

    return [schedule, refresh];
};

export const useStripePlans = () => {
    const [plans, setPlans] = useState<any>(null);
    useEffect(() => {
        const fn = async () => {
            try {
                const plans = await getPlans();

                setPlans(plans);
            } catch (error) {}
        };
        fn();
    }, []);
    return plans;
};

export const useStripeInvoice = () => {
    const [invoice, setInvoice] = useState<any>(null);
    useEffect(() => {
        let timer: any;
        let count = 0;
        const fn = async () => {
            try {
                const inv = await getInvoice();
                if (!inv) {
                    timer = setTimeout(fn, 1000);
                }
                setInvoice(inv);
            } catch (error) {
                //console.error("Error fetching invoice:", error);
                if (count < 5) {
                    timer = setTimeout(fn, 1000);
                    count++;
                }
            }
        };
        fn();
        return () => {
            if (timer) {
                clearTimeout(timer);
            }
        };
    }, []);
    return invoice;
};
export * from "./fitnessData";
export * from "./dimensions";
